import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { EVALUATION_STATUSES, MENU_ACTIONS, PAGE_NAMES, STATUS_TYPES } from '@/constants';
import { ActionMenuItem, FilterItem, ProjectDTO, SelectionOptions, UserDTO } from '@/models';
import {
  buildProjectTypeOptions,
  getUserDisplayInfo,
  fetchProjectsOfCurrentUser,
  filterProjectsMultiFieldsByKeywords,
  pagingProjectCard,
  fetchAssessmentPeriods
} from '@/services';
import { sortArrayByStatusField } from '@/helpers';
import ActionMenu from '@/components/common/action-menu/ActionMenu.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import ProjectCard from '@/components/pages/project-list/project-card/ProjectCard.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import ProjectCardsPagination from '@/components/pages/project-list/project-cards-pagination/ProjectCardsPagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import FilterList from '@/components/common/filter-list/FilterList.vue';
import DateRangeInput from '@/components/common/date-range-input/DateRangeInput.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';

@Component({
  components: {
    ActionMenu,
    InlineLoader,
    ProjectCard,
    ProjectCardsPagination,
    FilterDropdown,
    SearchInput,
    FilterList,
    DateRangeInput,
    Pagination
  }
})
export default class ProjectList extends Vue {
  private projects: ProjectDTO[] = [];
  private isProjectsFetching = true;
  private isAssessmentPeriodsFetching = true;
  private isOpenRequiredPeriodDialog = false;

  private selectedSponsorIDs: string[] = [];
  private selectedManagerIDs: string[] = [];
  private selectedStatuses: string[] = [];
  private selectedTypes: string[] = [];
  private selectedAssessmentIDs: string[] = [];

  private rangeDate: string[] = [];

  private searchKeys: string[] = [];

  private page = 1;
  private itemsPerPage = 10;

  private statusOptions: SelectionOptions = [
    { text: 'NOT STARTED', value: EVALUATION_STATUSES.NOT_STARTED.label },
    { text: 'ON GOING', value: EVALUATION_STATUSES.ON_GOING.label },
    { text: 'DONE', value: EVALUATION_STATUSES.DONE.label }
  ];
  private typeOptions: SelectionOptions = buildProjectTypeOptions();
  private assessmentOptions: SelectionOptions = [];

  get currentUser(): UserDTO {
    return this.$store.getters['app/currentUser'];
  }

  get menuItems(): ActionMenuItem[] {
    const userId = this.currentUser?.id;
    const hasProjectsToExport = this.projects.some(
      project => project.sponsor?.id === userId || project.manager?.id === userId
    );
    if (hasProjectsToExport) {
      return [{ label: MENU_ACTIONS.EXPORT, icon: 'mdi-file-table', handler: this.navigateToProjectExportPage }];
    }
    return [];
  }

  get sponsorOptions(): SelectionOptions {
    return this.projects
      .reduce(
        (uniPrj: ProjectDTO[], pj: ProjectDTO) => (uniPrj.find(p => p?.id === pj?.id) ? uniPrj : [...uniPrj, pj]),
        []
      )
      .map((project: ProjectDTO) => ({
        text: getUserDisplayInfo(project.sponsor),
        value: '' + project.sponsor?.id
      }));
  }

  get managerOptions(): SelectionOptions {
    return this.projects
      .reduce(
        (uniPrj: ProjectDTO[], pj: ProjectDTO) => (uniPrj.find(p => p?.id === pj?.id) ? uniPrj : [...uniPrj, pj]),
        []
      )
      .map(project => ({
        text: getUserDisplayInfo(project.manager),
        value: '' + project.manager?.id
      }));
  }

  get displayProjects(): ProjectDTO[] {
    const {
      projects,
      selectedSponsorIDs,
      selectedManagerIDs,
      selectedStatuses,
      selectedTypes,
      selectedAssessmentIDs,
      rangeDate,
      searchKeys
    } = this;
    let result = projects;

    this.page = 1;

    if (selectedSponsorIDs.length) {
      result = result.filter(project => selectedSponsorIDs.includes('' + project.sponsor?.id));
    }
    if (selectedManagerIDs.length) {
      result = result.filter(project => selectedManagerIDs.includes('' + project.manager?.id));
    }
    if (selectedStatuses.length) {
      result = result.filter(project => selectedStatuses.includes(project.projectStatus || ''));
    }
    if (selectedTypes.length) {
      result = result.filter(project => selectedTypes.includes(project.projectType));
    }
    if (selectedAssessmentIDs.length) {
      result = result.filter(project =>
        project.projectAssessments.some(assessment =>
          selectedAssessmentIDs.includes('' + assessment.assessmentPeriodId)
        )
      );
    }

    if (rangeDate) {
      if (rangeDate.length) {
        result = result.filter(
          project =>
            moment(project.startDate).isBetween(rangeDate[0], rangeDate[1], undefined, '[]') ||
            moment(project.endDate).isBetween(rangeDate[0], rangeDate[1], undefined, '[]')
        );
      }
    }

    const searchFields = ['id', 'name', 'sponsor.fullName', 'manager.fullName'];
    return filterProjectsMultiFieldsByKeywords(result, searchKeys, searchFields);
  }

  get paginationProjectCards(): ProjectDTO[] {
    return pagingProjectCard(this.displayProjects, this.page, this.itemsPerPage);
  }

  get pageCount(): number {
    return Math.ceil(this.displayProjects.length / this.itemsPerPage) || 1;
  }

  navigateToProjectExportPage(): void {
    this.$router.push({ name: PAGE_NAMES.PROJECT_EXPORT });
  }

  navigateToAssessmentPeriodsPage(): void {
    this.$router.push({ name: PAGE_NAMES.ASSESSMENT_PERIODS });
  }

  closeRequiredPeriodDialog(): void {
    this.isOpenRequiredPeriodDialog = false;
  }

  get isFiltered(): boolean {
    return !!(
      this.selectedSponsorIDs.length ||
      this.selectedManagerIDs.length ||
      this.selectedStatuses.length ||
      this.selectedTypes.length ||
      this.selectedAssessmentIDs.length
    );
  }

  get filterItems(): FilterItem[] {
    return [
      {
        component: FilterDropdown,
        label: 'Sponsor',
        disabled: this.isProjectsFetching,
        items: this.sponsorOptions,
        value: this.selectedSponsorIDs,
        onInput: this.onSponsorChanged
      },
      {
        component: FilterDropdown,
        label: 'PM',
        disabled: this.isProjectsFetching,
        items: this.managerOptions,
        value: this.selectedManagerIDs,
        onInput: this.onManagerChanged
      },
      {
        component: FilterDropdown,
        label: 'Status',
        disabled: this.isProjectsFetching,
        items: this.statusOptions,
        value: this.selectedStatuses,
        onInput: this.onStatusChanged
      },
      {
        component: FilterDropdown,
        label: 'Type',
        disabled: this.isProjectsFetching,
        items: this.typeOptions,
        value: this.selectedTypes,
        onInput: this.onTypeChanged
      },
      {
        component: DateRangeInput,
        label: 'Date Range',
        disabled: this.isProjectsFetching,
        value: this.rangeDate,
        items: this.rangeDate,
        onInput: this.changeRangeDate
      },
      {
        component: FilterDropdown,
        label: 'Assessment Period',
        disabled: this.isProjectsFetching || this.isAssessmentPeriodsFetching,
        items: this.assessmentOptions,
        value: this.selectedAssessmentIDs,
        onInput: this.onAssessmentPeriodChanged
      }
    ];
  }
  resetFilter(): void {
    this.selectedSponsorIDs = [];
    this.selectedManagerIDs = [];
    this.selectedStatuses = [];
    this.selectedTypes = [];
    this.rangeDate = [];
    this.selectedAssessmentIDs = [];
  }

  changeRangeDate(rangeDate: Array<string>): void {
    if (moment(rangeDate[0]) > moment(rangeDate[1])) {
      this.rangeDate = rangeDate.reverse();
    }
    this.rangeDate = rangeDate;
  }

  resetRangeDate(): void {
    this.rangeDate = ['', ''];
  }

  onSponsorChanged(selectedSponsorIDs: string[]): void {
    this.selectedSponsorIDs = selectedSponsorIDs;
  }

  onManagerChanged(selectedManagerIDs: string[]): void {
    this.selectedManagerIDs = selectedManagerIDs;
  }

  onStatusChanged(selectedStatuses: string[]): void {
    this.selectedStatuses = selectedStatuses;
  }

  onTypeChanged(selectedTypes: string[]): void {
    this.selectedTypes = selectedTypes;
  }

  onAssessmentPeriodChanged(selectedAssessmentIDs: string[]): void {
    this.selectedAssessmentIDs = selectedAssessmentIDs;
  }

  onSearch(searchTextChunks: string): void {
    this.searchKeys = searchTextChunks.split(',');
  }

  async navigateToCreateNewProject(): Promise<void> {
    this.$store.commit('project/setProject', null);
    await this.$router.push({ name: PAGE_NAMES.PROJECT_CREATION });
  }

  async mounted(): Promise<void> {
    fetchProjectsOfCurrentUser()
      .then(({ data }) => {
        this.projects = sortArrayByStatusField(data || [], 'projectStatus', STATUS_TYPES.EVALUATION);
      })
      .finally(() => {
        this.isProjectsFetching = false;
      });

    fetchAssessmentPeriods()
      .then(({ data }) => {
        this.assessmentOptions = (data || []).map(assessment => ({
          text: assessment.name,
          value: '' + (assessment?.id || '')
        }));
      })
      .finally(() => {
        this.isAssessmentPeriodsFetching = false;
      });
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
    this.page = 1;
  }

  get isCurrentUserAdmin(): boolean {
    return this.currentUser?.isAdmin ?? false;
  }
}
